import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { Container, Col, Row } from 'react-bootstrap';
import { Form, FormRenderProps, FormType } from '@enterprise/common';
import { SpotButton, SpotRadio, SpotRadioGroup } from '@enterprise/spot';
import { Field } from 'react-final-form';
import { observer } from 'mobx-react';
import { ApplicationInfoCard } from '../../../components/application/ApplicationInfoCard';
import { ProTips } from '../../../components/helpers/ProTips';
import { RedirectToRouteComponent } from '../../../router';
import { useStores } from '../../../hooks';

interface PageProps {
    returnRoute: string;
}

interface ClassificationsDatabase {
    masterDatabase: string;
}

export const ClassificationsDatabaseMappingPage = observer(function ClassificationsDatabaseMappingPageComponent(props: PageProps) {
    const { returnRoute } = props;
    // Access nested stores lazily, as the class getters did.
    const { domain, ui } = useStores();
    const store = domain.masterDataPageStore;

    // Created once per mounted component, as the class instance fields were.
    const dataRef = useRef<ClassificationsDatabase>({ masterDatabase: '' });
    const formRef = useRef<FormType<ClassificationsDatabase> | null>(null);
    if (formRef.current === null) {
        formRef.current = new FormType<ClassificationsDatabase>();
    }

    useEffect(() => {
        void store.loadMappableSites();

        return () => {
            store.dispose();
        };
    }, [store]);

    const onCancel = () => {
        ui.router.push(returnRoute);
    };

    const onSubmit = (data) => {
        if (data && data.masterDatabase) {
            store.useApplicationClassesAndSubclassesAsMaster(data.masterDatabase);
        }
    };

    return store.isLoaded ? (
        store.areClassificationsMapped ? (
            <RedirectToRouteComponent to={returnRoute} />
        ) : (
            <Container className={'classifications-database-mapping'}>
                <Row className={'margin-top-4'}>
                    <Col xs={1} />
                    <Col xs={11}>
                        <h1 className="classifications-header">
                            <Trans i18nKey="masterData:classificationsPage.databaseMappingsTitle">Choose your master database</Trans>
                        </h1>
                    </Col>
                </Row>
                <Row className={'margin-top-3'}>
                    <Col xs={1} />
                    <Col xs={11}>
                        <Form formType={formRef.current} data={dataRef.current} onSubmit={onSubmit}>
                            {({ handleSubmit }: FormRenderProps<ClassificationsDatabase>) => (
                                <>
                                    {store.mappableSites.map((site) => (
                                        <SpotRadioGroup isHorizontal={true} key={`${site.id}`}>
                                            <Field name="masterDatabase" type="radio" value={`${site.id}`}>
                                                {({ input }) => (
                                                    <SpotRadio {...input} value={`${site.id}`} isCentered={true} isFullWidth={true}>
                                                        <ApplicationInfoCard
                                                            app={site}
                                                            applicationDetailsRoute={null}
                                                            listPracticeNames={false}
                                                            isManageable={false}
                                                            toggleApplicationActive={null}
                                                        />
                                                    </SpotRadio>
                                                )}
                                            </Field>
                                        </SpotRadioGroup>
                                    ))}

                                    <SpotButton isPrimary={true} disabled={false} onClick={handleSubmit}>
                                        <Trans i18nKey="masterData:classificationsPage.submitMaster">Submit Master</Trans>
                                    </SpotButton>

                                    <button className="spot-modal__footer-cancel-button cancel-button" onClick={onCancel}>
                                        <Trans i18nKey="common:cancel">cancel</Trans>
                                    </button>
                                </>
                            )}
                        </Form>
                    </Col>
                </Row>
            </Container>
        )
    ) : (
        <ProTips />
    );
});
